// WS Now consumes issue times from the provider, never the browser clock.
export const NATIONAL_TRACKING_BOUNDS = [4.5, 33.5, 20.5, 49.5];

export function parseObservationTime(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (!value) return null;
    const normalized = String(value).trim().replace(' ', 'T');
    const result = Date.parse(/[zZ]$|[+-]\d\d:?\d\d$/.test(normalized) ? normalized : normalized + 'Z');
    return Number.isFinite(result) ? result : null;
}

export function collectionBaseTime(collection) {
    const raw = collection?.metadata?.base_time_utc ?? collection?.meta?.base_time_utc
        ?? collection?.base_time_utc ?? collection?.features?.[0]?.properties?.base_time_utc
        ?? collection?.features?.[0]?.properties?.run_time_utc;
    return parseObservationTime(raw);
}

export function validNationalPoint(feature) {
    const c = feature?.geometry?.coordinates;
    const [w, s, e, n] = NATIONAL_TRACKING_BOUNDS;
    return feature?.geometry?.type === 'Point' && Array.isArray(c)
        && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1])
        && c[0] >= w && c[0] <= e && c[1] >= s && c[1] <= n;
}

function distanceKm(a, b) {
    const rad = Math.PI / 180;
    const lat = (a[1] + b[1]) * rad / 2;
    return Math.hypot((a[0] - b[0]) * 111.32 * Math.cos(lat), (a[1] - b[1]) * 111.32);
}

// Keep the radar track as the canonical object when an electrical cell overlaps.
// The association adds electrical evidence; it does not turn a nearby rain echo
// into a severity alert. Unmatched electrical objects remain visible nationally.
export function mergeNationalCells(radarCells, electricalCells) {
    const now = Date.now();
    const associationWindowMs = 20 * 60000;
    const radar = (radarCells?.features || []).filter(validNationalPoint).map(feature => ({
        ...feature, properties: { ...feature.properties, _track_source: 'national-radar' },
    }));
    const electrical = [];
    for (const feature of (electricalCells?.features || []).filter(validNationalPoint)) {
        const p = feature.properties || {};
        const electricalTime = parseObservationTime(p.last_seen_at_utc);
        const associationRadius = Math.max(5, Math.min(12, Number(p.radius_km) || 8));
        let matched = null, nearest = Infinity;
        for (const candidate of radar) {
            const radarTime = parseObservationTime(candidate.properties.last_seen_at_utc ?? candidate.properties.base_time_utc);
            // Spatial overlap alone cannot confirm a current radar cell with
            // an old electrical event. Both source times must be known.
            if (electricalTime === null || radarTime === null
                || now - electricalTime > associationWindowMs || electricalTime > now + 60000
                || radarTime > now + 60000 || Math.abs(radarTime - electricalTime) > associationWindowMs) continue;
            const distance = distanceKm(candidate.geometry.coordinates, feature.geometry.coordinates);
            if (distance <= associationRadius && distance < nearest) { matched = candidate; nearest = distance; }
        }
        if (matched) {
            const target = matched.properties;
            const hasLightning = p.has_lightning === true || Number(p.detection_count) > 0;
            target.has_lightning = target.has_lightning === true || hasLightning;
            target.detection_count = Number(target.detection_count || 0) + Number(p.detection_count || 0);
            target.flash_rate_per_min = Math.max(Number(target.flash_rate_per_min || 0), Number(p.flash_rate_per_min || 0));
            if (hasLightning) {
                target.storm_type = 'thunderstorm';
                target.display_label_it = 'Cella radar con attività elettrica associata';
                target.signal_sources = 'Radar DPC · Blitzortung';
                target.lightning_cell_ids = [...(target.lightning_cell_ids || []), p.id];
            }
        } else electrical.push({ ...feature, properties: { ...p, _track_source: 'lightning' } });
    }
    return { type: 'FeatureCollection', features: [...radar, ...electrical] };
}

export function forecastAtLead(feature, leadMinutes) {
    const points = (feature?.properties?.forecast_points || []).filter(point =>
        point.prospective !== false && Number.isFinite(Number(point.lead_minutes)) && Number(point.lead_minutes) >= 0
        && Array.isArray(point.coordinates) && point.coordinates.slice(0, 2).every(Number.isFinite))
        .sort((a, b) => Number(a.lead_minutes) - Number(b.lead_minutes));
    if (!Number.isFinite(leadMinutes) || leadMinutes < 0 || !points.length) return null;
    // Prospective flags distinguish actual future predictions from leads that
    // had already elapsed when a delayed radar run was issued.
    const hasProspectivePolicy = (feature?.properties?.forecast_points || []).some(point => typeof point.prospective === 'boolean');
    if (hasProspectivePolicy && leadMinutes < Number(points[0].lead_minutes)) return null;
    const origin = feature?.geometry?.coordinates?.[0];
    if (Number(points[0].lead_minutes) > 0 && Array.isArray(origin) && origin.slice(0, 2).every(Number.isFinite)) {
        points.unshift({ lead_minutes: 0, coordinates: origin, uncertainty_km: null });
    }
    if (leadMinutes < Number(points[0].lead_minutes) || leadMinutes > Number(points.at(-1).lead_minutes)) return null;
    const exact = points.find(point => Number(point.lead_minutes) === leadMinutes);
    if (exact) return { ...exact, lead_minutes: leadMinutes, uncertainty_km: exact.uncertainty_km ?? null };
    const hi = points.findIndex(point => Number(point.lead_minutes) > leadMinutes);
    if (hi <= 0) return null;
    const a = points[hi - 1], b = points[hi];
    const t = (leadMinutes - Number(a.lead_minutes)) / (Number(b.lead_minutes) - Number(a.lead_minutes));
    return {
        coordinates: [a.coordinates[0] + (b.coordinates[0] - a.coordinates[0]) * t,
            a.coordinates[1] + (b.coordinates[1] - a.coordinates[1]) * t],
        lead_minutes: leadMinutes,
        uncertainty_km: Number.isFinite(a.uncertainty_km) && Number.isFinite(b.uncertainty_km)
            ? a.uncertainty_km + (b.uncertainty_km - a.uncertainty_km) * t : null,
    };
}

export function forecastHorizon(collection) {
    return Math.max(0, ...(collection?.features || []).flatMap(feature =>
        (feature.properties?.forecast_points || []).filter(point => point.prospective !== false).map(point => Number(point.lead_minutes) || 0)));
}
